import dayjs from 'dayjs';
import { Utils } from '@gv/triage-components';
import { createApi } from '@reduxjs/toolkit/query/react';

import { Config } from 'config';
import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { acceptanceApi, acceptanceTags } from 'store/api/acceptance';

import { generateItems } from '../helpers';
import {
  EventsResponse,
  VitrualExamsListRequest,
  VirtualExamsCreateRequest,
  VirtualExamsDeleteRequest,
} from '../types';

import { VitrualExamsListResponse } from './types';

const tagTypes = ['VirtualExams'];

export const virtualExamsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'virtualExamsApi',
  endpoints: (build) => ({
    delete: build.mutation<void, VirtualExamsDeleteRequest>({
      invalidatesTags: tagTypes,
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        await cacheDataLoaded;
        dispatch(acceptanceApi.util.invalidateTags(acceptanceTags));
      },
      query: ({ id, slot, docId, groupId, hospitalId }) => ({
        method: 'delete',
        url: Utils.Query.addParamsToUrl(URL.VIRTUAL_EXAMS_SCHEDULE, {
          id,
          doctor_id: docId,
          group_id: groupId,
          hospital_id: hospitalId,
          slot: JSON.stringify(slot),
        }),
      }),
    }),

    list: build.query<EventsResponse, VitrualExamsListRequest>({
      providesTags: tagTypes,
      keepUnusedDataFor: Config.portalType.isPetOwner ? 1 : undefined,
      transformResponse: (response: VitrualExamsListResponse, meta, arg) => {
        const data: any = response?.data || {};
        if (arg.localTimezone) {
          data.timezone = { time_zone_code: dayjs.tz.guess() };
        }
        return generateItems(data);
      },
      query: ({ ids, end, start, hospitalId, includeDoctorInfo }) => ({
        method: 'get',
        url: Utils.Query.addParamsToUrl(URL.VIRTUAL_EXAMS_SCHEDULE, {
          end_date: end,
          start_date: start,
          includeDoctorInfo,
          hospital_id: hospitalId,
          doctor_ids: JSON.stringify(ids),
        }),
      }),
    }),

    create: build.mutation<void, VirtualExamsCreateRequest>({
      invalidatesTags: tagTypes,
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        await cacheDataLoaded;
        dispatch(acceptanceApi.util.invalidateTags(acceptanceTags));
      },
      query: ({
        slot,
        docId,
        every,
        endDate,
        startDate,
        hospitalId,
        clinicDays,
        ...props
      }) => ({
        method: 'post',
        url: URL.VIRTUAL_EXAMS_SCHEDULE,
        data: {
          doctor_id: docId,
          end_date: endDate,
          every: String(every),
          start_date: startDate,
          hospital_id: String(hospitalId),
          time_slots: JSON.stringify(slot),
          clinic_days: JSON.stringify(clinicDays),
          ...props,
        },
      }),
    }),
  }),
});

export { tagTypes as virtualExamsTags };
